import {motion} from 'framer-motion'
import {usePatientContext} from './StyleDetailInformationWrapper'

import {ReactComponent as BiBody} from '../../../assets/icons/BiBody.svg'
import {ReactComponent as IoChatBubbles} from '../../../assets/icons/IoChatBubbles.svg'
import {ReactComponent as SettingIcon} from '../../../assets/icons/SettingIcon.svg'
import {ReactComponent as TbCameraPlus} from '../../../assets/icons/TbCameraPlus.svg'
import {ReactComponent as AssignHCP} from '../../../assets/menu/AssignHCP.svg'

import {ReactComponent as DobIcon} from '../../../assets/icons/DobIcon.svg'
import {ReactComponent as HospitalIcon} from '../../../assets/icons/HospitalIcon.svg'
import {ReactComponent as HospitalNumberIcon} from '../../../assets/icons/HospitalNumberIcon.svg'
import {ReactComponent as NHSNumberIcon} from '../../../assets/icons/NHSNumberIcon.svg'

import {ReactComponent as WatchFace} from '../../../assets/icons/MdWatchFace.svg'
import {ReactComponent as WatchIcon} from '../../../assets/icons/WatchIcon.svg'

import {ReactComponent as AppleWatchRotate} from '../../../assets/watch/apple-watch-rotate.svg'
import {ReactComponent as AppleWatch} from '../../../assets/watch/AW-3.svg'

import {ReactComponent as NotWornSamsungWatch} from '../../../assets/watch/NotWornSamsungWatch.svg'
import {ReactComponent as SamsungWatchFace} from '../../../assets/watch/SamsungWatchFace.svg'
import {ReactComponent as WornSamsungWatch} from '../../../assets/watch/WornSamsungWatch.svg'

import {ReactComponent as WatchIconDisabled} from '../../../assets/icons/MdWatchDisabled.svg'
import {ReactComponent as WatchUnassigned} from '../../../assets/icons/MdWatchUnassigned.svg'
import {ReactComponent as BatteryCharging} from '../../../assets/icons/SiBatteryCharging.svg'
import {ReactComponent as BatteryEmpty} from '../../../assets/icons/SiBatteryEmpty.svg'
import {ReactComponent as BatteryFull} from '../../../assets/icons/SiBatteryFull.svg'
import {ReactComponent as BatteryHalf} from '../../../assets/icons/SiBatteryHalf.svg'
import {ReactComponent as BatteryHigh} from '../../../assets/icons/SiBatteryHigh.svg'
import {ReactComponent as BatteryInUse} from '../../../assets/icons/SiBatteryInUse.svg'
import {ReactComponent as BatteryLow} from '../../../assets/icons/SiBatteryLow.svg'
import {ReactComponent as BatteryUnknown} from '../../../assets/icons/SiBatteryUnknown.svg'
import {ReactComponent as DeviceAssigned} from '../../../assets/icons/SiCircleTick.svg'
import {ReactComponent as OnWristIcon} from '../../../assets/watch/OnWrist.svg'
import {ReactComponent as SpatchIcon} from '../../../assets/watch/SpatchIcon.svg'
import {ReactComponent as TakeOffIcon} from '../../../assets/watch/TakeOff.svg'

import {ReactComponent as FaDoctor} from '../../../assets/icons/FaDoctor.svg'
import {ReactComponent as FaThermometerHalf} from '../../../assets/icons/FaThermometerHalf.svg'
import {ReactComponent as MdOutlineAssignmentTurnedIn} from '../../../assets/icons/MdOutlineAssignmentTurnedIn.svg'

import {Button} from 'primereact/button'
import {Divider} from 'primereact/divider'
import {Image} from 'primereact/image'
import {Tag} from 'primereact/tag'
import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import defaultAvatar from '../../../assets/images/avatar.jpg'
import {capitalize} from '../../../utils/CommonUtils'

import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {SplitButton} from 'primereact/splitbutton'
import {TabPanel, TabView} from 'primereact/tabview'
import {SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import useWindowSize from '../../../utils/useWindowSize'
import {useAuth} from '../../authentication/AuthProvider'
import {getClientId} from '../../authentication/AuthUtils'
import WatchRemoteActivation from '../../authentication/WatchRemoteActivation'
import BiometricsForm from '../../biometrics/BiometricsForm'
import WatchLine from '../../detail/WatchLine'
import {ECGDeviceTypes} from '../../ecg/ECGConstants'
import StyledTitle from '../../layout/StyledTitle'
import SendMessageForm from '../../message/SendMessageForm'
import {permissionOptionKeys} from '../../role/RoleConstant'
import SummaryTable from '../../summary/SummaryTable'
import {convertKeyToLabel} from '../../table-utils/TableUtils'
import StyledAssignmentHealthCarePractitioner from '../StyledAssignmentHealthCarePractitioner'
import StyledAssignmentPathway from '../StyledAssignmentPathway'
import StyledAssignmentStaticList from '../StyledAssignmentStaticList'
import StyledAssignmentSupportTeam from '../StyledAssignmentSupportTeam'
import '../styles.css'
import {
    getSupportTeamEventKey,
    supportTeamEvent,
    supportTeamEventTypes,
} from '../support-team/broadcast/SupportTeamEvent'
import MapCurrentLocation from './MapCurrentLocation'
import StyledChangeAvatarForm from './StyledChangeAvatarForm'
import StyledPatientPortalSettingsForm from './StyledPatientPortalSettingsForm'

const iconStyle = {
    width: '18px',
    height: '18px',
    color: '#6c757d',
    marginRight: '6px',
}

const statusMapping = {
    normal: {severity: 'success', value: 'NORMAL'},
    mild: {severity: 'info', value: 'MILD'},
    moderate: {severity: 'warning', value: 'MODERATE'},
    severe: {severity: 'danger', value: 'SEVERE'},
}

const StyledBasicInformation = React.memo(({isFullScreen = false}) => {
    //#region Context
    const {currentViewedUser, handleCheckPermissionByKey} = usePatientContext()
    const {user} = useAuth()
    const {userRole, viewOptions} = useAppConfig()
    const {currentHospital} = useUserHospital()
    //#endregion
    const windowSize = useWindowSize()

    const queryClient = useQueryClient()
    const changeAvatarRef = useRef(null)
    const biometricsRef = useRef(null)
    const hcpAssignmentRef = useRef(null)
    const staticListAssignmentRef = useRef(null)
    const pathwayAssignmentRef = useRef(null)
    const supportTeamRef = useRef(null)
    const summaryRef = useRef(null)
    const patientPortalSettingRef = useRef(null)
    const sendMessageRef = useRef(null)
    //#region Information
    const basicInformationList = useMemo(
        () => [
            {
                label: 'NHS Number',
                value: currentViewedUser?.nhsNumber,
                icon: <HospitalIcon />,
            },
            {
                label: 'Gender',
                value: currentViewedUser?.gender,
                icon: <HospitalIcon />,
            },
            {
                label: 'Age',
                value: currentViewedUser?.age
                    ? `${currentViewedUser?.age} years old`
                    : '-',
                icon: <HospitalIcon />,
            },
            {
                label: 'Date of birth',
                value: currentViewedUser?.dob,
                icon: <DobIcon />,
            },
            {
                label: 'Hospital Name',
                value: currentViewedUser?.hospitalName,
                icon: <HospitalIcon />,
            },
            {
                label: 'Hospital Number',
                value: currentViewedUser?.hospitalNumber,
                icon: <HospitalNumberIcon />,
            },
            {
                label: 'Last Known Location',
                value: currentViewedUser?.lastLocation,
                icon: <NHSNumberIcon />,
            },

            {
                label: 'Current Location',
                value: currentViewedUser?.currentLocation,
                icon: 'pi pi-map-marker',
            },
            {
                label: 'Arrival Date',
                value: currentViewedUser?.createdAt
                    ? currentViewedUser?.createdAt
                    : '-',
                icon: <NHSNumberIcon />,
            },

            {
                label: 'Discharge Date',
                value: currentViewedUser?.dischargedAt,
                icon: 'pi pi-map-marker',
            },
        ],
        [currentViewedUser],
    )
    //#endregion

    //#region Menu - More Actions
    const items = [
        {
            label: (
                <span className="font-bold text-darkcyan">
                    Profile Settings
                </span>
            ),
            disabled: true,
        },
        {
            label: 'Change Avatar',
            icon: <TbCameraPlus style={iconStyle} />,
            visible: handleCheckPermissionByKey(
                permissionOptionKeys.allowToEdit,
            ),
            command: () => {
                changeAvatarRef.current.showForm()
            },
        },
        {
            label: 'Edit BMI',
            icon: <BiBody style={iconStyle} />,
            visible: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAddBiometrics,
            ),
            command: () => {
                biometricsRef.current.showForm(
                    currentViewedUser ? {...currentViewedUser?.biometrics} : {},
                )
            },
        },
        {
            separator: true,
        },
        {
            label: (
                <span className="font-bold text-darkcyan">
                    Patient Portal Settings
                </span>
            ),
            disabled: true,
        },
        {
            label: 'Change Setting',
            icon: <SettingIcon style={iconStyle} />,

            command: () => patientPortalSettingRef.current.showForm(),
        },
        {
            separator: true,
        },
        {
            label: <span className="font-bold text-darkcyan">Assignment</span>,
            disabled: true,
        },
        {
            label: 'Edit Support Team',
            icon: <AssignHCP style={iconStyle} />,

            command: () => supportTeamRef.current.showForm(),
        },
        {
            label: 'Edit Pathway',
            icon: <FaThermometerHalf style={iconStyle} />,
            visible: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAssignPathway,
            ),
            command: () => pathwayAssignmentRef.current.showForm(),
        },
        {
            label: 'Edit HC.Practitioner',
            icon: <FaDoctor style={iconStyle} />,
            visible: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAssignHcp,
            ),
            command: () => hcpAssignmentRef.current.showForm(),
        },
        {
            label: 'Edit Static List',
            icon: <MdOutlineAssignmentTurnedIn style={iconStyle} />,
            visible: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAssignStaticList,
            ),
            command: () => staticListAssignmentRef.current.showForm(),
        },
        {
            separator: true,
        },
        {
            label: <span className="font-bold text-darkcyan">Contact</span>,
            disabled: true,
        },
        {
            label: 'Send Message',
            icon: <IoChatBubbles style={iconStyle} />,
            command: () => sendMessageRef.current.showForm(currentViewedUser),
        },
    ]
    //#endregion

    //#region Event Listener on State Changes
    useEffect(() => {
        // USER SUPPORT TEAM CHANGE
        supportTeamEvent.on(
            getSupportTeamEventKey(
                supportTeamEventTypes.USER_SUPPORT_TEAM_CHANGE,
            ),
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    userRole === SystemRole.USER_WITH_ROLE &&
                    (!data?.hospitalId ||
                        data?.hospitalId !== currentHospital?.id)
                ) {
                    return
                }

                if (currentViewedUser?.id !== data?.userId) {
                    return
                }

                const {supportTeamKeys} = QueryKeyManager(
                    QueryKeyTypes.supportTeam,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: supportTeamKeys.pattern(),
                })
            },
        )

        return () => {
            supportTeamEvent.removeListener(
                getSupportTeamEventKey(
                    supportTeamEventTypes.USER_SUPPORT_TEAM_CHANGE,
                ),
            )
        }
    }, [])

    return (
        <div className="w-full">
            {/* Patient Information */}
            <div className="grid relative h-full">
                <div
                    className={`${
                        isFullScreen ? 'lg:col-4 md:col-5' : 'lg:col-5 md:col-5'
                    }  col-12`}>
                    <div
                        className={`flex ${
                            isFullScreen
                                ? 'justify-content-center gap-5'
                                : 'flex-column justify-content-between'
                        } align-items-center mb-3`}>
                        {/* Avatar */}
                        <div>
                            <Image
                                src={
                                    currentViewedUser?.avatar
                                        ? currentViewedUser?.avatar
                                        : defaultAvatar
                                }
                                alt="Avatar"
                                width={150}
                                height={150}
                            />
                        </div>

                        <div className="flex flex-column align-items-center justify-content-between ">
                            {' '}
                            <h3 className="font-bold mb-1 text-center">
                                {currentViewedUser?.title &&
                                currentViewedUser.title !== 'unknown'
                                    ? `${capitalize(currentViewedUser?.title)} `
                                    : ''}
                                {currentViewedUser?.fullName
                                    ? capitalize(currentViewedUser?.fullName)
                                    : '-'}
                            </h3>
                            <p className="text-600 font-semibold text-sm">
                                {currentViewedUser?.email}
                            </p>
                            {handleCheckPermissionByKey(
                                permissionOptionKeys.allowToViewSummary,
                            ) && (
                                <Button
                                    className="text-sm bg-blue-50 hover:bg-blue-100 border-none text-blue-500"
                                    iconPos="left"
                                    icon="pi pi-eye"
                                    label={'View Summary'}
                                    onClick={() =>
                                        summaryRef.current.showForm()
                                    }
                                />
                            )}
                        </div>
                    </div>

                    <div className="col-12">
                        <h5 className="font-semibold text-gray-500  mb-1 uppercase text-lg font-bold text-center">
                            APPOINTMENTS
                        </h5>
                        <div className="col-12 flex justify-content-center">
                            <div className="w-full text-center">
                                <h3 className="font-bold text-darkcyan mb-1">
                                    {currentViewedUser?.pastAppointment || 0}
                                </h3>
                                <p className="font-semibold text-gray-4">
                                    Past
                                </p>
                            </div>
                            <Divider layout="vertical" />
                            <div className="w-full  text-center">
                                <h3 className="font-bold text-darkcyan mb-1">
                                    {currentViewedUser?.upcomingAppointment ||
                                        0}
                                </h3>
                                <p className="font-semibold text-gray-4">
                                    Upcoming
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="col-12">
                        <SplitButton
                            menuClassName="custom-disability"
                            className="col-12 px-0"
                            model={items}
                            severity="info"
                            icon="pi pi-ellipsis-v"
                            label="MORE ACTIONS"
                        />
                    </div>
                </div>
                <div
                    className={`grid align-items-strecth ${
                        isFullScreen ? 'lg:col-8 md:col-7' : 'lg:col-7 md:col-7'
                    }  col-12`}>
                    {basicInformationList?.map((item, index) => (
                        <div
                            key={`${item?.label}-${index}`}
                            className={`${
                                isFullScreen
                                    ? 'lg:col-20 md:col-4 sm:col-6 col-12'
                                    : 'lg:col-6 md:col-6 sm:col-6 col-12'
                            }`}>
                            <div className="h-full border-bottom-1 border-100 w-full">
                                <h4 className="font-semibold text-gray-500  mb-1  text-sm font-bold">
                                    {item?.icon ? (
                                        typeof item.icon === 'string' ? (
                                            <i
                                                className={`${item.icon} mr-2`}></i>
                                        ) : (
                                            <span className="mr-2">
                                                {item.icon}
                                            </span>
                                        )
                                    ) : (
                                        ''
                                    )}
                                    {<span>{item?.label}</span>}
                                </h4>
                                <h5 className="font-bold ">
                                    {item?.value ? item.value : '-'}
                                </h5>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Summary Status */}
                {!!statusMapping[currentViewedUser?.sicknessStatus]
                    ?.severity && (
                    <motion.div
                        className="absolute top-0 left-0"
                        animate={{
                            opacity: [1, 0.6, 1],
                        }}
                        transition={{
                            repeat: Infinity,
                            duration: 1.5,
                            repeatType: 'loop',
                        }}>
                        <Tag
                            className="px-3"
                            severity={
                                statusMapping[currentViewedUser?.sicknessStatus]
                                    .severity
                            }
                            value={
                                statusMapping[currentViewedUser?.sicknessStatus]
                                    .value
                            }
                        />
                    </motion.div>
                )}
            </div>

            {/* Form Ref */}

            {/* Change Avatar Form */}
            <StyledChangeAvatarForm ref={changeAvatarRef} />

            {/* Biometric Form */}
            <BiometricsForm ref={biometricsRef} />

            {/* Assign Health Care Practitioner Form */}
            <StyledAssignmentHealthCarePractitioner
                userIds={!!currentViewedUser?.id ? [currentViewedUser?.id] : []}
                ref={hcpAssignmentRef}
            />

            {/* Assign Static List Form */}
            <StyledAssignmentStaticList
                userIds={!!currentViewedUser?.id ? [currentViewedUser?.id] : []}
                ref={staticListAssignmentRef}
            />

            {/* Assign Pathway Form */}
            <StyledAssignmentPathway
                userIds={!!currentViewedUser?.id ? [currentViewedUser?.id] : []}
                ref={pathwayAssignmentRef}
            />

            {/* Support Team Form */}
            <StyledAssignmentSupportTeam ref={supportTeamRef} />

            {/* Summary Form */}
            <SummaryTable ref={summaryRef} />

            {/* Patient Portal Setting Form */}
            <StyledPatientPortalSettingsForm ref={patientPortalSettingRef} />

            {/* Message Form */}
            <SendMessageForm ref={sendMessageRef} />
        </div>
    )
})

const watchMode = {fullWatch: 'fullWatch', watchFace: 'watchFace'}

const DeviceBrand = {
    samsung: ECGDeviceTypes.samsung,
    apple: ECGDeviceTypes.apple,
}

const StyledWatchInformation = React.memo(() => {
    useSignals()

    //#region Context
    const {currentViewedUser} = usePatientContext()
    //#endregion

    //#region Hooks
    const {translateKeys, translateCommons} = TranslateUtils()
    //#endregion

    //#region Watch View Mode
    const deviceBrand = useMemo(() => {
        if (!currentViewedUser?.deviceBrand) {
            return DeviceBrand.samsung
        }

        if (currentViewedUser?.deviceBrand === DeviceBrand.apple) {
            return DeviceBrand.apple
        }

        if (currentViewedUser?.deviceBrand === DeviceBrand.samsung) {
            return DeviceBrand.samsung
        }

        return DeviceBrand.samsung
    }, [currentViewedUser?.deviceBrand])

    const tabViewRef = useRef(null)

    const handleOnBeforeTabChange = index => {
        if (tabViewRef.current) {
            tabViewRef.current.activeIndex = index
        }
    }

    const watchViewModeTemplate = (template, options) => {
        return (
            <div
                className="flex cursor-pointer "
                onClick={() => options?.onClick()}>
                <div className="mr-2">
                    <span>{template.icon}</span>
                </div>
                <div>
                    <span className="font-bold">
                        {translateCommons(convertKeyToLabel(template.value))}
                    </span>
                </div>
            </div>
        )
    }

    const watchViewModeOptions = [
        {
            icon: <WatchIcon style={{width: 15, height: 15}} />,
            value: watchMode.fullWatch,
        },
        {
            icon: <WatchFace style={{width: 15, height: 15}} />,
            value: watchMode.watchFace,
        },
    ]
    //#endregion

    const messageWatchInteractionContent = useMemo(() => {
        if (!currentViewedUser?.id || !currentViewedUser?.deviceAssigned) {
            return null
        }

        return (
            <div
                className="flex align-items-center justify-content-between w-full"
                style={{minHeight: '2.5vh'}}>
                <div className="mr-2">
                    {!!currentViewedUser?.accessConfig?.watchInteraction ? (
                        <i
                            className="pi pi-check-circle"
                            style={{height: 18, width: 18}}></i>
                    ) : (
                        <WatchIconDisabled
                            style={{height: 18, width: 18}}
                            className="mr-2"
                        />
                    )}
                </div>
                <div>
                    <span className="font-bold">
                        {translateCommons('Interaction mode is')} {''}
                        {currentViewedUser?.accessConfig?.watchInteraction ? (
                            <span className="text-darkcyan">
                                {translateCommons('enabled')}
                            </span>
                        ) : (
                            <span style={{color: 'red'}}>
                                {translateCommons('disabled')}
                            </span>
                        )}
                    </span>
                </div>
            </div>
        )
    }, [
        currentViewedUser?.accessConfig?.watchInteraction,
        currentViewedUser?.deviceAssigned,
        currentViewedUser?.id,
        translateCommons,
    ])

    const activeIndexTabView = useSignal(1)

    return (
        <div className="grid">
            <div className="mb-1 bg-white p-0 border-round-top-lg md:col-6 col-12">
                <StyledTitle
                    title={translateCommons('Watch Status')}
                    description={
                        'The following information is based on the latest data from the watch to indicate the current status of the watch.'
                    }
                    icon={
                        <WatchIcon
                            style={{
                                height: 25,
                                width: 25,
                            }}
                        />
                    }
                />

                {/* switch btn */}
                <div className="col-12 lg:col-6 md:col-6 p-0 mb-3">
                    <div className="flex gap-1 col-12 pt-0">
                        {watchViewModeOptions.map((item, idx) => (
                            <Button
                                key={idx}
                                className={`${
                                    activeIndexTabView.value !== idx
                                        ? 'bg-gray-50 text-teal-700 border-gray-100 hover:text-teal-600'
                                        : 'bg-teal-600 border-teal-600'
                                } w-full md:w-10rem lg:w-10rem`}
                                label={translateCommons(
                                    convertKeyToLabel(item.value),
                                )}
                                icon={item.icon}
                                onClick={() => {
                                    activeIndexTabView.value = idx
                                    handleOnBeforeTabChange(idx)
                                }}
                            />
                        ))}
                    </div>
                </div>
                <TabView
                    activeIndex={activeIndexTabView.value}
                    onTabChange={handleOnBeforeTabChange}
                    className="watch-status-tab-wrapper">
                    <TabPanel
                        headerClassName="watch-status-tab-header border-radius-left hidden"
                        headerTemplate={options =>
                            watchViewModeTemplate(
                                watchViewModeOptions[0],
                                options,
                            )
                        }>
                        <StyledWatchWrapper>
                            <div className="flex justify-content-center w-full">
                                <div>{messageWatchInteractionContent}</div>
                            </div>
                            {!!currentViewedUser?.id &&
                                (!currentViewedUser?.deviceAssigned ||
                                    !currentViewedUser?.beingWorn) && (
                                    <div
                                        className="flex align-items-center justify-content-center w-full h-25rem"
                                        style={{zIndex: 10}}>
                                        {deviceBrand === DeviceBrand.apple ? (
                                            <AppleWatchRotate
                                                style={{
                                                    width: '100%',
                                                    height: '250px',
                                                }}
                                            />
                                        ) : (
                                            <NotWornSamsungWatch
                                                style={{
                                                    width: '75%',
                                                    height: '250px',
                                                }}
                                            />
                                        )}
                                    </div>
                                )}

                            {!!currentViewedUser?.id &&
                                !!currentViewedUser?.deviceAssigned &&
                                !!currentViewedUser?.beingWorn && (
                                    <div
                                        className="flex align-items-center justify-content-center w-full"
                                        style={{
                                            transform: 'rotate(0deg)',
                                            zIndex: 10,
                                        }}>
                                        {deviceBrand === DeviceBrand.apple ? (
                                            <AppleWatchRotate
                                                style={{
                                                    width: '50%',
                                                    height: '250px',
                                                }}
                                            />
                                        ) : (
                                            <WornSamsungWatch
                                                style={{
                                                    width: '70%',
                                                    height: '250px',
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            {/* Watch Assignment Status */}
                            <div className="flex justify-content-center w-full">
                                {currentViewedUser?.deviceAssigned &&
                                    currentViewedUser?.watchDeviceName && (
                                        <p className="text-center">
                                            {translateCommons(
                                                'The watch model is ',
                                            )}{' '}
                                            <strong>
                                                {
                                                    currentViewedUser?.watchDeviceName
                                                }
                                            </strong>
                                        </p>
                                    )}
                            </div>
                        </StyledWatchWrapper>
                    </TabPanel>
                    <TabPanel
                        headerClassName="watch-status-tab-header border-radius-right hidden"
                        headerTemplate={options =>
                            watchViewModeTemplate(
                                watchViewModeOptions[1],
                                options,
                            )
                        }>
                        {' '}
                        <StyledWatchWrapper>
                            <div className="flex justify-content-center w-full">
                                <div>{messageWatchInteractionContent}</div>
                            </div>
                            <div
                                className="flex align-items-center justify-content-center w-full h-27rem"
                                style={{zIndex: 10}}>
                                {deviceBrand === DeviceBrand.apple ? (
                                    <AppleWatch
                                        style={{
                                            width: '70%',
                                            height: '250px',
                                        }}
                                    />
                                ) : (
                                    <SamsungWatchFace
                                        style={{
                                            width: '70%',
                                            height: '250px',
                                        }}
                                    />
                                )}
                            </div>
                            {/* Watch Assignment Status */}
                            <div className="flex justify-content-center w-full">
                                {currentViewedUser?.deviceAssigned &&
                                    currentViewedUser?.watchDeviceName && (
                                        <p className="text-center">
                                            {translateCommons(
                                                'The watch model is ',
                                            )}{' '}
                                            <strong>
                                                {
                                                    currentViewedUser?.watchDeviceName
                                                }
                                            </strong>
                                        </p>
                                    )}
                            </div>
                        </StyledWatchWrapper>{' '}
                    </TabPanel>
                </TabView>
            </div>

            <div className="md:col-6 col-12 border-round-lg p-3">
                <MapCurrentLocation user={currentViewedUser} />
            </div>
        </div>
    )
})

const StyledWatchWrapper = React.memo(({type, children}) => {
    const windowSize = useWindowSize()
    const responsiveLayoutTag = useMemo(() => {
        const defaultLayoutStyle = {
            assigned: {
                top: '2rem',
                left: '0.5rem',
            },
            interaction: {
                left: '0.5rem',
                bottom: '0rem',
            },
            wornStatus: {
                top: '12rem',
                left: '0.5rem',
            },
            isCharging: {
                top: '12rem',
                right: '0.5rem',
            },
            batteryLevel: {
                top: '2rem',
                right: '0.5rem',
            },
            spatchIntegration: {
                bottom: '0rem',
                right: '0.5rem',
            },
        }

        const xlargeLayout = {
            assigned: {
                top: '2rem',
                left: '0.5rem',
            },
            interaction: {
                left: '0.5rem',
                bottom: '0rem',
            },
            wornStatus: {
                top: '12rem',
                left: '0.5rem',
            },
            isCharging: {
                top: '12rem',
                right: '0.5rem',
            },
            batteryLevel: {
                top: '2rem',
                right: '0.5rem',
            },
            spatchIntegration: {
                bottom: '0rem',
                right: '0.5rem',
            },
        }

        const largeLayout = {
            assigned: {
                top: '2rem',
                left: '0.5rem',
            },
            interaction: {
                left: '0.5rem',
                bottom: '0rem',
            },
            wornStatus: {
                top: '12rem',
                left: '0.5rem',
            },
            isCharging: {
                top: '12rem',
                right: '0.5rem',
            },
            batteryLevel: {
                top: '2rem',
                right: '0.5rem',
            },
            spatchIntegration: {
                bottom: '0rem',
                right: '0.5rem',
            },
        }

        const mobileLayout = {
            assigned: {
                top: '0',
                left: 'auto',
                right: 'auto',
            },
            isCharging: {
                top: '5rem',
                right: '0',
            },
            batteryLevel: {
                top: '10rem',
                left: '0',
            },
            spatchIntegration: {
                bottom: '0',
                right: 'auto',
                left: 'auto',
            },
            wornStatus: {
                left: '0',
                bottom: '5rem',
            },
            interaction: {
                bottom: '10rem',
                right: '0',
            },
        }

        const smallLayout = {
            assigned: {
                top: '2rem',
                left: '0.5rem',
            },
            interaction: {
                left: '0.5rem',
                bottom: '0rem',
            },
            wornStatus: {
                top: '12rem',
                left: '0.5rem',
            },
            isCharging: {
                top: '12rem',
                right: '0.5rem',
            },
            batteryLevel: {
                top: '2rem',
                right: '0.5rem',
            },
            spatchIntegration: {
                bottom: '0rem',
                right: '0.5rem',
            },
        }

        const mediumLayout = {
            assigned: {
                top: '2rem',
                left: '0.5rem',
            },
            interaction: {
                left: '0.5rem',
                bottom: '0rem',
            },
            wornStatus: {
                top: '12rem',
                left: '0.5rem',
            },
            isCharging: {
                top: '12rem',
                right: '0.5rem',
            },
            batteryLevel: {
                top: '2rem',
                right: '0.5rem',
            },
            spatchIntegration: {
                bottom: '0rem',
                right: '0.5rem',
            },
        }

        if (windowSize > 2600) {
            return defaultLayoutStyle
        }

        if (windowSize > 2299 && windowSize <= 2600) {
            return xlargeLayout
        }

        if (windowSize > 1920 && windowSize <= 2299) {
            return largeLayout
        }

        if (windowSize > 1500 && windowSize <= 1920) {
            return mediumLayout
        }

        if (windowSize <= 500) {
            return mobileLayout
        } else if (windowSize < 1500) {
            return smallLayout
        } else {
            return defaultLayoutStyle
        }
    }, [windowSize])
    //#region Context
    const {currentViewedUser} = usePatientContext()
    const {viewOptions} = useAppConfig()
    //#endregion

    //#region Hooks
    const {translateKeys, translateCommons} = TranslateUtils()
    //#endregion

    //#region Watch Activation Form
    const watchRemoteActivationRef = useRef(null)

    const handleShowWatchActivationForm = useCallback(() => {
        watchRemoteActivationRef.current?.showForm()
    }, [])
    //#endregion

    //#region Watch Status Labels
    const watchStatusLabels = useMemo(() => {
        const iconStyle = {
            width: '24px',
            height: '24px',
        }

        let watchStatus = {
            assigned: {
                id: 'watch-assigned-label',
                header: translateCommons('Assignment Status'),
                label: (
                    <small>
                        <span>
                            <Tag
                                id={'watch-assigned-label'}
                                severity="danger"
                                value={translateCommons(
                                    'No watch has been assigned',
                                )}
                            />
                        </span>{' '}
                        {translateCommons('to this patient')}
                    </small>
                ),
                icon: <WatchUnassigned style={{...iconStyle}} />,
                value: false,
                style: {
                    ...responsiveLayoutTag?.assigned,
                    zIndex: 1,
                },
            },

            wornStatus: {
                id: 'watch-worn-status-label',
                header: translateCommons('On-Wrist Status'),
                label: (
                    <small>
                        <span>
                            <Tag
                                id={'watch-worn-status-label'}
                                severity="warning"
                                value={translateCommons('Unknown')}
                            />
                        </span>
                    </small>
                ),
                icon: <WatchIcon style={{...iconStyle}} />,
                value: false,
                style: {
                    ...responsiveLayoutTag?.wornStatus,
                    zIndex: 1,
                },
            },

            interaction: {
                id: 'watch-interaction-label',
                header: translateCommons('Watch Interaction'),
                label: (
                    <small>
                        <span>
                            <Tag
                                id={'watch-interaction-label'}
                                severity="warning"
                                value={translateCommons('Unknown')}
                            />
                        </span>
                    </small>
                ),
                icon: <WatchIcon style={{...iconStyle}} />,
                value: null,
                style: {
                    ...responsiveLayoutTag?.interaction,
                    zIndex: 1,
                },
            },
            isCharging: {
                id: 'watch-charging-label',
                header: translateCommons('Charging State'),
                label: (
                    <small>
                        <span>
                            <Tag
                                id={'watch-charging-label'}
                                severity="warning"
                                value={translateCommons('Unknown')}
                            />
                        </span>
                    </small>
                ),
                icon: <BatteryUnknown style={{...iconStyle}} />,
                value: null,
                style: {
                    ...responsiveLayoutTag?.isCharging,
                    zIndex: 1,
                },
            },
            batteryLevel: {
                id: 'watch-battery-level-label',
                value: null,
                header: translateCommons('Battery Life'),
                icon: <BatteryUnknown style={{...iconStyle}} />,
                label: (
                    <small>
                        <span>
                            <Tag
                                id={'watch-battery-level-label'}
                                severity="warning"
                                value={translateCommons('Unknown')}
                            />
                        </span>
                    </small>
                ),
                style: {
                    ...responsiveLayoutTag?.batteryLevel,
                    zIndex: 1,
                },
            },
            spatchIntegration: {
                id: 'watch-spatch-integration-label',
                header: translateCommons('S-Patch Integration'),
                label: (
                    <small>
                        <span>
                            <Tag
                                id={'watch-spatch-integration-label'}
                                severity="warning"
                                value={translateCommons('Unknown')}
                            />
                        </span>
                    </small>
                ),
                icon: (
                    <SpatchIcon style={{...iconStyle, width: 32, height: 32}} />
                ),
                value: null,
                style: {
                    ...responsiveLayoutTag?.spatchIntegration,
                    zIndex: 1,
                },
            },
        }

        if (
            !currentViewedUser ||
            !currentViewedUser?.id ||
            !currentViewedUser?.deviceAssigned
        ) {
            return watchStatus
        }

        return {
            ...watchStatus,
            assigned: {
                ...watchStatus.assigned,
                label: (
                    <small>
                        {translateCommons('This patient has been')}{' '}
                        <span>
                            {' '}
                            <Tag
                                id={watchStatus.assigned?.id}
                                severity="success"
                                value={translateCommons('assigned')}
                            />
                        </span>{' '}
                        {translateCommons('to the watch')}
                    </small>
                ),
                icon: <DeviceAssigned style={{...iconStyle}} />,
                value: currentViewedUser?.deviceAssigned,
            },
            wornStatus: {
                ...watchStatus.wornStatus,
                icon: currentViewedUser?.beingWorn ? (
                    <OnWristIcon style={{...iconStyle}} />
                ) : (
                    <TakeOffIcon style={{...iconStyle}} />
                ),
                label: (
                    <small>
                        {currentViewedUser?.deviceBrand ===
                            ECGDeviceTypes.apple && (
                            <span>
                                <Tag
                                    id={watchStatus.wornStatus?.id}
                                    severity="warning"
                                    value={translateCommons('Not Available')}
                                />
                            </span>
                        )}

                        {currentViewedUser?.deviceBrand ===
                            ECGDeviceTypes.samsung && (
                            <React.Fragment>
                                {translateCommons('The watch is ')}
                                {currentViewedUser?.beingWorn ? (
                                    <span>
                                        <Tag
                                            id={watchStatus.wornStatus?.id}
                                            severity="success"
                                            value={translateCommons(
                                                'being worn now',
                                            )}
                                        />
                                    </span>
                                ) : (
                                    <span>
                                        <Tag
                                            id={watchStatus.wornStatus?.id}
                                            severity="danger"
                                            value={translateCommons(
                                                'not being worn',
                                            )}
                                        />
                                    </span>
                                )}
                            </React.Fragment>
                        )}
                    </small>
                ),
                value: currentViewedUser?.beingWorn,
            },
            interaction: {
                ...watchStatus.interaction,
                label: (
                    <small>
                        {translateCommons('The watch')}{' '}
                        {translateCommons('Interaction mode is')} {''}
                        <span>
                            <Tag
                                id={watchStatus.interaction?.id}
                                severity={`${
                                    currentViewedUser?.accessConfig
                                        ?.watchInteraction
                                        ? 'success'
                                        : 'warning'
                                }`}
                                value={`${
                                    currentViewedUser?.accessConfig
                                        ?.watchInteraction
                                        ? translateCommons('enabled')
                                        : translateCommons('disabled')
                                }`}
                            />
                        </span>
                    </small>
                ),
                icon: !!currentViewedUser?.accessConfig?.watchInteraction ? (
                    <WatchIcon style={{...iconStyle}} />
                ) : (
                    <WatchIconDisabled style={{...iconStyle}} />
                ),
                value: null,
            },
            batteryLevel: {
                ...watchStatus.batteryLevel,
                label: (
                    <small>
                        {translateCommons('The current battery level is')}{' '}
                        <span>
                            <Tag
                                id={watchStatus.batteryLevel?.id}
                                severity="success"
                                value={`${currentViewedUser?.batteryLife}%`}></Tag>
                        </span>
                    </small>
                ),
                value: currentViewedUser?.batteryLife,
                icon:
                    currentViewedUser?.batteryLife >= 75 ? (
                        <BatteryFull style={{...iconStyle}} />
                    ) : currentViewedUser?.batteryLife >= 50 ? (
                        <BatteryHigh style={{...iconStyle}} />
                    ) : currentViewedUser?.batteryLife >= 25 ? (
                        <BatteryHalf style={{...iconStyle}} />
                    ) : currentViewedUser?.batteryLife >= 10 ? (
                        <BatteryLow style={{...iconStyle}} />
                    ) : currentViewedUser?.batteryLife < 1 ? (
                        <BatteryEmpty style={{...iconStyle}} />
                    ) : (
                        <BatteryUnknown style={{...iconStyle}} />
                    ),
            },
            isCharging: {
                ...watchStatus.isCharging,
                label: (
                    <small>
                        {translateCommons('The watch')} is{' '}
                        {currentViewedUser?.isCharging ? (
                            <span>
                                <Tag
                                    id={watchStatus.isCharging?.id}
                                    severity="warning"
                                    value={translateCommons('charging')}
                                />
                            </span>
                        ) : (
                            <span>
                                <Tag
                                    id={watchStatus.isCharging?.id}
                                    severity="success"
                                    value={translateCommons('in use')}
                                />
                            </span>
                        )}
                    </small>
                ),
                value: currentViewedUser?.charging,
                icon: currentViewedUser?.charging ? (
                    <BatteryCharging style={{...iconStyle}} />
                ) : (
                    <BatteryInUse style={{...iconStyle}} />
                ),
            },
            spatchIntegration: {
                ...watchStatus.spatchIntegration,
                label: (
                    <small>
                        {currentViewedUser?.deviceBrand ===
                            DeviceBrand.apple && (
                            <span>
                                <Tag
                                    id={watchStatus.spatchIntegration?.id}
                                    severity={'warning'}
                                    value={'Not Available'}
                                />
                            </span>
                        )}

                        {currentViewedUser?.deviceBrand ===
                            DeviceBrand.samsung && (
                            <React.Fragment>
                                <span>
                                    <Tag
                                        id={watchStatus.spatchIntegration?.id}
                                        severity={
                                            currentViewedUser?.spatchDeviceName
                                                ? 'success'
                                                : 'warning'
                                        }
                                        value={
                                            currentViewedUser?.spatchDeviceName
                                                ? 'Already integrated'
                                                : 'Not integrated'
                                        }
                                    />
                                </span>
                                {currentViewedUser?.spatchDeviceName ? (
                                    <span>
                                        {` `}with S-Patch{' '}
                                        {currentViewedUser?.spatchDeviceName}
                                    </span>
                                ) : (
                                    <span>{` `}with S-Patch yet</span>
                                )}
                            </React.Fragment>
                        )}
                    </small>
                ),
            },
        }
    }, [
        currentViewedUser,
        responsiveLayoutTag?.assigned,
        responsiveLayoutTag?.batteryLevel,
        responsiveLayoutTag?.interaction,
        responsiveLayoutTag?.isCharging,
        responsiveLayoutTag?.spatchIntegration,
        responsiveLayoutTag?.wornStatus,
        translateCommons,
    ])
    //#endregion
    const watchViewDisplayType = {
        normal: 'normal',
        splitView: 'splitView',
    }
    const watchDisplayMode = useMemo(() => {
        if (viewOptions) {
            if (viewOptions === watchViewDisplayType.splitView) {
                return watchViewDisplayType.splitView
            }
        }

        return watchViewDisplayType.normal
    }, [
        viewOptions,
        watchViewDisplayType.normal,
        watchViewDisplayType.splitView,
    ])

    return (
        <div
            className={`${
                watchDisplayMode === watchViewDisplayType.splitView && 'grid'
            } w-full p-0`}>
            {/* Watch Image */}
            <div
                className={`${
                    watchDisplayMode === watchViewDisplayType.splitView
                        ? 'col-12'
                        : 'col-12'
                } relative grid p-0`}>
                {children}
                <div className="grid absolute col-12 px-2 h-full p-0 m-0 my-3">
                    {' '}
                    {!!currentViewedUser?.id && (
                        <React.Fragment>
                            {watchStatusLabels &&
                                Object.keys(watchStatusLabels).map(
                                    (key, index) => (
                                        <React.Fragment key={index}>
                                            {[1, 3, 5].includes(index) && (
                                                <div className="col-4"></div>
                                            )}
                                            <div
                                                className={`${
                                                    watchDisplayMode ===
                                                    watchViewDisplayType.normal
                                                        ? 'white-space-nowrap lg:absolute w-auto '
                                                        : 'col-4 p-0'
                                                } `}
                                                style={
                                                    watchDisplayMode ===
                                                    watchViewDisplayType.normal
                                                        ? {
                                                              ...watchStatusLabels[
                                                                  key
                                                              ]?.style,
                                                              background:
                                                                  'white',
                                                          }
                                                        : {}
                                                }>
                                                <motion.div
                                                    id={`${watchStatusLabels[key]?.id}-wrapper`}
                                                    key={`${watchStatusLabels[key]?.id}-wrapper-${index}`}
                                                    className={`${
                                                        watchDisplayMode ===
                                                            watchViewDisplayType.splitView &&
                                                        'col-12 '
                                                    } samsung-watch-label px-3 watch-label-wrapper pointer`}
                                                    style={{
                                                        border: '1px solid #f5f5f5',
                                                        borderRadius: 8,
                                                    }}
                                                    // key={`${watchStatusLabels[key]?.id}-wrapper-${index}`}
                                                    whileHover={{
                                                        boxShadow:
                                                            'rgba(149, 157, 165, 0.2) 0px 8px 24px',
                                                    }}>
                                                    <div className="flex justify-content-between">
                                                        <div className="">
                                                            <h6 className=" font-semibold mb-2">
                                                                {
                                                                    watchStatusLabels[
                                                                        key
                                                                    ]?.header
                                                                }
                                                            </h6>
                                                            {
                                                                watchStatusLabels[
                                                                    key
                                                                ]?.label
                                                            }
                                                        </div>{' '}
                                                        <div className="">
                                                            {' '}
                                                            <div className="  p-2 bg-white border-round-lg sm:text-xs text-green-600">
                                                                {
                                                                    watchStatusLabels[
                                                                        key
                                                                    ]?.icon
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            </div>{' '}
                                        </React.Fragment>
                                    ),
                                )}

                            <WatchLine
                                showWatchActivationForm={
                                    handleShowWatchActivationForm
                                }
                                isDrawable={
                                    watchDisplayMode !==
                                    watchViewDisplayType.splitView
                                }
                                isAddHoverEffect={
                                    watchDisplayMode !==
                                    watchViewDisplayType.splitView
                                }
                            />
                        </React.Fragment>
                    )}
                </div>
            </div>
            {/* Watch Information */}
            <div
                className={`${
                    watchDisplayMode === watchViewDisplayType.normal
                        ? ' flex gap-1 overflow-x-scroll lg:overflow-x-visible watch-view-normal-wrapper'
                        : 'col-12 lg:col-6'
                } `}>
                <div className="grid hidden">
                    {' '}
                    {!!currentViewedUser?.id && (
                        <React.Fragment>
                            {watchStatusLabels &&
                                Object.keys(watchStatusLabels).map(
                                    (key, index) => (
                                        <div
                                            className={`${
                                                watchDisplayMode ===
                                                watchViewDisplayType.normal
                                                    ? 'white-space-nowrap lg:absolute w-auto '
                                                    : 'col-6 '
                                            } `}
                                            style={
                                                watchDisplayMode ===
                                                watchViewDisplayType.normal
                                                    ? {
                                                          ...watchStatusLabels[
                                                              key
                                                          ]?.style,
                                                          background: 'white',
                                                      }
                                                    : {}
                                            }
                                            key={index}>
                                            <motion.div
                                                id={`${watchStatusLabels[key]?.id}-wrapper`}
                                                key={`${watchStatusLabels[key]?.id}-wrapper-${index}`}
                                                className={`${
                                                    watchDisplayMode ===
                                                        watchViewDisplayType.splitView &&
                                                    'col-12 '
                                                } samsung-watch-label px-3 watch-label-wrapper pointer bg-gray-50  `}
                                                // key={`${watchStatusLabels[key]?.id}-wrapper-${index}`}
                                                whileHover={{
                                                    boxShadow:
                                                        'rgba(149, 157, 165, 0.2) 0px 8px 24px',
                                                }}>
                                                <div className="flex justify-content-between">
                                                    <div className="">
                                                        <h6 className=" font-semibold mb-0">
                                                            {
                                                                watchStatusLabels[
                                                                    key
                                                                ]?.header
                                                            }
                                                        </h6>
                                                        {
                                                            watchStatusLabels[
                                                                key
                                                            ]?.label
                                                        }
                                                    </div>{' '}
                                                    <div className="">
                                                        {' '}
                                                        <div className="  p-2 bg-white border-round-lg sm:text-xs text-green-600">
                                                            {
                                                                watchStatusLabels[
                                                                    key
                                                                ]?.icon
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        </div>
                                    ),
                                )}

                            <WatchLine
                                showWatchActivationForm={
                                    handleShowWatchActivationForm
                                }
                                isDrawable={
                                    watchDisplayMode !==
                                    watchViewDisplayType.splitView
                                }
                                isAddHoverEffect={
                                    watchDisplayMode !==
                                    watchViewDisplayType.splitView
                                }
                            />
                        </React.Fragment>
                    )}
                </div>
            </div>

            <div>
                {/* Watch Activation Form */}
                <WatchRemoteActivation
                    ref={watchRemoteActivationRef}
                    accessCode={currentViewedUser?.accessCode}
                />
            </div>
        </div>
    )
})

const StyledDetailInformation = React.memo(({profileFullScreen = false}) => {
    return (
        <div className="w-full">
            <div className="grid h-full">
                {!profileFullScreen && (
                    <div className="col-12 px-0">
                        <StyledWatchInformation />
                    </div>
                )}
            </div>
        </div>
    )
})

export default StyledDetailInformation
